import { createContext, useState, useCallback, ReactNode, useContext } from 'react';
import { ModalType, ModalState, ModalPropsMap } from 'components/Modal/Modal.types';

interface ModalContextValue {
  modal: ModalState;
  openModal: <T extends ModalType>(
    type: T,
    ...args: keyof ModalPropsMap[T] extends never
      ? []
      : [ModalPropsMap[T]]
  ) => void;
  closeModal: () => void;
};

const ModalContext = createContext<ModalContextValue | null>(null);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modal, setModal] = useState<ModalState>({ type: null });

  const openModal = <T extends ModalType>(
    type: T,
    ...args: keyof ModalPropsMap[T] extends never
      ? []
      : [ModalPropsMap[T]]
  ) => {
    setModal(
      (args.length === 0
        ? { type }
        : { type, props: args[0] }
      ) as ModalState
    );
  };

  const closeModal = useCallback(() => {
    setModal({ type: null });
  }, []);

  return (
    <ModalContext.Provider value={{ modal, openModal, closeModal }}>
      {children}
    </ModalContext.Provider>
  );
};

export const useModalContext = () => {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error('useModalContext must be used inside ModalProvider');
  }

  return context;
};